import { useEffect, useMemo, useState } from 'react';
import { Clock3, Gift, PlayCircle, ShieldCheck, X } from 'lucide-react';
import {
  formatCountdown,
  getOfferRemainingMs,
  getOrCreateOfferExpiryIso,
  OFFER_CODE,
  OFFER_MODAL_DELAY_MS,
  OFFER_MODAL_SEEN_KEY,
} from '../utils/offerCampaign';

export default function LeadOfferModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [offerExpiryIso, setOfferExpiryIso] = useState('');
  const [remainingMs, setRemainingMs] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const alreadySeen = window.sessionStorage.getItem(OFFER_MODAL_SEEN_KEY) === '1';
    if (alreadySeen) return undefined;

    const timerId = window.setTimeout(() => {
      const expiry = getOrCreateOfferExpiryIso();
      setOfferExpiryIso(expiry);
      setRemainingMs(getOfferRemainingMs(expiry));
      setIsOpen(true);
    }, OFFER_MODAL_DELAY_MS);

    return () => window.clearTimeout(timerId);
  }, []);

  useEffect(() => {
    if (!isOpen || !offerExpiryIso) return undefined;

    const tick = () => setRemainingMs(getOfferRemainingMs(offerExpiryIso));
    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [isOpen, offerExpiryIso]);

  const countdown = useMemo(() => formatCountdown(remainingMs), [remainingMs]);
  const isExpired = remainingMs <= 0;

  if (!isOpen) return null;

  const closeModal = (keepSeen = false) => {
    if (!keepSeen && typeof window !== 'undefined') {
      window.sessionStorage.setItem(OFFER_MODAL_SEEN_KEY, '0');
    }
    setIsOpen(false);
  };

  const startApplication = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(OFFER_MODAL_SEEN_KEY, '1');
    }
    closeModal(true);
    window.setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.location.href = '/basvuru';
      }
    }, 120);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 backdrop-blur-sm px-4 py-8">
      <div className="w-full max-w-4xl rounded-3xl border border-slate-200/20 bg-white dark:bg-slate-900 shadow-2xl overflow-hidden relative">
        <button
          type="button"
          onClick={closeModal}
          className="absolute right-4 top-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-300/70 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          aria-label="Kapat"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="p-7 lg:p-8 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-slate-800">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-3 py-1.5 text-xs font-bold mb-4">
              <Gift className="w-4 h-4" />
              DEMO TEKLİFİ
            </div>

            <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-3">
              Çıkmadan önce kısa bir demo başvurusu bırak
            </h3>

            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              Başvuru formunu doldurduğunda, canlı demo planlaması için ekibimiz seninle hızlıca iletişime geçer.
              Kampanya kodun otomatik olarak başvuruna işlenir.
            </p>

            <div className="space-y-3 text-sm mb-6">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">Hızlı geri dönüş</p>
                  <p className="text-slate-500 dark:text-slate-400">Başvurular admin panelde "başvuru" tipi ile ayrışır.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <PlayCircle className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">Demo odaklı akış</p>
                  <p className="text-slate-500 dark:text-slate-400">Kısa form ile demo ihtiyacını net şekilde iletirsin.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-1">Kampanya Kodu</p>
                <p className="font-black text-primary">{OFFER_CODE}</p>
              </div>
              <div className="rounded-xl border border-amber-300/60 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/30 p-3">
                <p className="text-[11px] text-amber-700 dark:text-amber-300 mb-1">Teklif Durumu</p>
                <p className="font-black text-amber-700 dark:text-amber-300">{isExpired ? 'Sure doldu' : 'Aktif'}</p>
              </div>
            </div>
          </div>

          <div className="p-7 lg:p-8 bg-slate-50 dark:bg-slate-950/50">
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 aspect-video mb-5 flex items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-3 w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center">
                  <PlayCircle className="w-7 h-7" />
                </div>
                <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Demo tanıtım görüntüsü</p>
              </div>
            </div>

            <div className="rounded-2xl border border-amber-300/60 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/20 p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500 text-white flex items-center justify-center">
                  <Clock3 className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs font-bold tracking-wide uppercase text-amber-700 dark:text-amber-300">
                    Teklifin sana özel, süre azalıyor
                  </p>
                  <p className="text-2xl font-black text-amber-700 dark:text-amber-300">{countdown}</p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={startApplication}
              disabled={isExpired}
              className="w-full rounded-xl bg-primary text-white font-bold py-4 disabled:opacity-60 disabled:cursor-not-allowed hover:brightness-105 transition"
            >
              {isExpired ? 'Teklif süresi doldu' : 'Hemen Ücretsiz Başla'}
            </button>
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center mt-3">
              Basvuruya yönlenince kampanya kodu formuna otomatik eklenir.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

